import React from 'react';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { useTranslation } from 'react-i18next';
import config from '../../config';
import { GitRefreshSummary } from '../../interfaces';

type Props = {
  gitLabApiBaseUrl: string;
  setGitLabApiBaseUrl: (value: string) => void;
  gitToken: string;
  loading: boolean;
  profileError: string | null;
  refreshActionError: string | null;
  operationId: string | null;
  refreshStatus: string | null;
  refreshCurrentAction: string | null;
  refreshPendingActions: number;
  refreshSummary: GitRefreshSummary | null;
  refreshProgress: number;
  onForceRefresh: () => void;
  apiError: string | null;
};

const GitAdminHeaderCard: React.FC<Props> = ({
  gitLabApiBaseUrl,
  setGitLabApiBaseUrl,
  gitToken,
  loading,
  profileError,
  refreshActionError,
  operationId,
  refreshStatus,
  refreshCurrentAction,
  refreshPendingActions,
  refreshSummary,
  refreshProgress,
  onForceRefresh,
  apiError
}) => {
  const { t } = useTranslation();

  return (
    <Card className="shadow-sm mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h2 className="mb-0">{t('gitAdmin.title')}</h2>
        <Button variant="primary" onClick={onForceRefresh} disabled={!gitToken || !gitLabApiBaseUrl?.trim() || loading}>{t('gitAdmin.refreshAll')}</Button>
      </Card.Header>
      <Card.Body>
        <Row className="g-3 mb-3">
          <Col md={8}>
            <Form.Group>
              <Form.Label>{t('gitAdmin.gitLabApiBaseUrl')}</Form.Label>
              <Form.Control
                value={gitLabApiBaseUrl}
                onChange={(event) => setGitLabApiBaseUrl(event.target.value)}
                placeholder={config.gitApiBaseUrl}
              />
            </Form.Group>
          </Col>
        </Row>
        {profileError && <Alert variant="warning">{profileError}</Alert>}
        {!gitToken && <Alert variant="warning">Token GitLab manquant sur votre profil. Le refresh Git est indisponible.</Alert>}
        {refreshActionError && <Alert variant="warning">{refreshActionError}</Alert>}
        {operationId && <Alert variant="info">{t('gitAdmin.refreshQueued')}: {operationId} ({t(`gitAdmin.status.${refreshStatus ?? 'queued'}`)})</Alert>}
        {operationId && (
          <div className="small text-muted mb-2">
            Action en cours: {refreshCurrentAction ?? 'N/A'} | Actions en attente: {refreshPendingActions}
          </div>
        )}
        {refreshSummary && refreshStatus === 'completed' && (
          <div className="small text-muted mb-2">
            Repositories: {refreshSummary.repositoriesUpdated} | Coverages: {refreshSummary.coveragesUpdated} | Issues: {refreshSummary.issuesUpdated} | Snapshots: {refreshSummary.snapshotsLogged}
          </div>
        )}
        {refreshProgress > 0 && (
          <ProgressBar
            now={refreshProgress}
            className="mb-3"
            animated={refreshStatus === 'running'}
            striped={refreshStatus === 'running' || refreshStatus === 'queued'}
            variant={refreshStatus === 'failed' ? 'danger' : 'primary'}
            label={`${refreshProgress}%`}
          />
        )}
        {apiError && <Alert variant="danger">{apiError}</Alert>}
        <div className="text-muted">{t('gitAdmin.pipelineHint')}</div>
      </Card.Body>
    </Card>
  );
};

export default GitAdminHeaderCard;
